import { create } from 'zustand';
import type {
  ProjectMeta,
  SkillInfo,
  TabId,
  TriageResult,
  SeparationResult,
  ResearchReport,
  ExtractionResult,
  BoqExtractionResult,
  FloorPlanAnnotationResult,
  ApprovalRequest,
  CablesResult,
} from '../types';

interface PdfNavigationTarget {
  file: string;
  page: number;
}

interface ProjectState {
  // Project metadata
  project: ProjectMeta | null;
  skills: SkillInfo[];
  sessionId: string | null;
  renders: Record<string, string[]>;

  // Skill results (loaded on demand / SSE updates)
  triage: TriageResult | null;
  separation: SeparationResult | null;
  researchReport: ResearchReport | null;
  extraction: ExtractionResult | null;
  boqExtraction: BoqExtractionResult | null;
  floorPlanAnnotation: FloorPlanAnnotationResult | null;
  preScan: unknown | null;
  cablesResult: CablesResult | null;

  // Floor plan annotation reviews (independent of extraction)
  fpPendingReviews: ApprovalRequest[];
  fpCurrentReview: ApprovalRequest | null;

  // UI
  sidebarCollapsed: boolean;
  activeTab: TabId;
  tabAttention: Partial<Record<TabId, boolean>>;
  pdfNavigationTarget: PdfNavigationTarget | null;
  toast: { message: string; type: 'success' | 'error' | 'info' } | null;
  // Timestamp set when an "analyze new files" run finishes, so the header
  // button + banner re-fetch new-files / incremental-status.
  incrementalAnalysisRefreshedAt: number;

  // Actions
  setProject: (p: ProjectMeta | null) => void;
  setSkills: (s: SkillInfo[]) => void;
  setSessionId: (id: string) => void;
  setRenders: (r: Record<string, string[]>) => void;
  setTriage: (t: TriageResult) => void;
  setSeparation: (s: SeparationResult) => void;
  setResearchReport: (r: ResearchReport) => void;
  setExtraction: (e: ExtractionResult) => void;
  setBoqExtraction: (b: BoqExtractionResult) => void;
  setFloorPlanAnnotation: (f: FloorPlanAnnotationResult) => void;
  setPreScan: (p: unknown) => void;
  setCablesResult: (c: CablesResult | null) => void;
  addFpPendingReview: (review: ApprovalRequest) => void;
  shiftFpPendingReview: () => ApprovalRequest | undefined;
  setFpCurrentReview: (review: ApprovalRequest | null) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setActiveTab: (tab: TabId) => void;
  setTabAttention: (tab: TabId, on: boolean) => void;
  navigateToPage: (file: string, page: number) => void;
  setToast: (msg: string | null, type?: 'success' | 'error' | 'info') => void;
  markIncrementalAnalysisRefreshed: () => void;
  clearAllResults: () => void;
}


export const useProjectStore = create<ProjectState>((set, get) => ({
  project: null,
  skills: [],
  sessionId: null,
  renders: {},
  triage: null,
  separation: null,
  researchReport: null,
  extraction: null,
  boqExtraction: null,
  floorPlanAnnotation: null,
  preScan: null,
  cablesResult: null,
  fpPendingReviews: [],
  fpCurrentReview: null,
  sidebarCollapsed: false,
  activeTab: 'pipeline',
  tabAttention: {},
  pdfNavigationTarget: null,
  toast: null,
  incrementalAnalysisRefreshedAt: 0,

  setProject: (project) => set({ project }),
  setSkills: (skills) => set({ skills }),
  setSessionId: (sessionId) => set({ sessionId }),
  setRenders: (renders) => set({ renders }),
  setTriage: (triage) => set({ triage }),
  setSeparation: (separation) => set({ separation }),
  setResearchReport: (researchReport) => set({ researchReport }),
  setExtraction: (extraction) => set({ extraction }),
  setBoqExtraction: (boqExtraction) => set({ boqExtraction }),
  setFloorPlanAnnotation: (floorPlanAnnotation) => set({ floorPlanAnnotation }),
  setPreScan: (preScan) => set({ preScan }),
  setCablesResult: (cablesResult) => set({ cablesResult }),
  addFpPendingReview: (review) =>
    set((s) => {
      if (s.fpPendingReviews.some((r) => r.request_id === review.request_id)) return s;
      if (s.fpCurrentReview?.request_id === review.request_id) return s;
      return { fpPendingReviews: [...s.fpPendingReviews, review] };
    }),
  shiftFpPendingReview: () => {
    const s = get();
    if (s.fpPendingReviews.length === 0) return undefined;
    const [first, ...rest] = s.fpPendingReviews;
    set({ fpPendingReviews: rest });
    return first;
  },
  setFpCurrentReview: (fpCurrentReview) => set({ fpCurrentReview }),
  setSidebarCollapsed: (sidebarCollapsed) => set({ sidebarCollapsed }),
  setActiveTab: (activeTab) => set((s) => ({
    activeTab,
    tabAttention: { ...s.tabAttention, [activeTab]: false },
  })),
  setTabAttention: (tab, on) => set((s) => ({
    tabAttention: { ...s.tabAttention, [tab]: on },
  })),
  navigateToPage: (file, page) => set({ pdfNavigationTarget: { file, page } }),
  setToast: (msg, type) => set({ toast: msg ? { message: msg, type: type || 'info' } : null }),
  markIncrementalAnalysisRefreshed: () => set({ incrementalAnalysisRefreshedAt: Date.now() }),
  clearAllResults: () =>
    set({
      triage: null,
      separation: null,
      researchReport: null,
      extraction: null,
      boqExtraction: null,
      floorPlanAnnotation: null,
      preScan: null,
      cablesResult: null,
      skills: [],
    }),
}));
